import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  MoreVertical,
  Pin,
  PinOff,
  Archive,
  ArchiveRestore,
  CheckCircle,
  Circle,
  Loader2,
} from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface ConversationActionsMenuProps {
  conversationId: Id<"conversations">;
  isRead: boolean;
  isPinned: boolean;
  isArchived: boolean;
  onActionComplete?: () => void;
}

export function ConversationActionsMenu({
  conversationId,
  isRead,
  isPinned,
  isArchived,
  onActionComplete,
}: ConversationActionsMenuProps) {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { toast } = useToast();

  const markAsRead = useMutation(api.messages.markAsRead);
  const markAsUnread = useMutation(api.messages.markAsUnread);
  const togglePin = useMutation(api.messages.togglePinConversation);
  const toggleArchive = useMutation(api.messages.toggleArchiveConversation);

  const handleAction = async (
    action: string,
    mutationFn: () => Promise<any>,
    successMessage: string
  ) => {
    setIsLoading(action);
    try {
      await mutationFn();
      toast({
        description: successMessage,
        duration: 2000,
      });
      onActionComplete?.();
    } catch (error) {
      console.error(`Error ${action}:`, error);
      toast({
        description: `Failed to ${action}. Please try again.`,
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleMarkReadUnread = () => {
    const action = isRead ? "mark as unread" : "mark as read";
    const mutationFn = isRead
      ? () => markAsUnread({ conversationId })
      : () => markAsRead({ conversationId });
    const successMessage = isRead ? "Conversation marked as unread" : "Conversation marked as read";

    handleAction(action, mutationFn, successMessage);
  };

  const handleTogglePin = () => {
    const action = isPinned ? "unpin" : "pin";
    const mutationFn = () => togglePin({ conversationId });
    const successMessage = isPinned ? "Conversation unpinned" : "Conversation pinned";

    handleAction(action, mutationFn, successMessage);
  };

  const handleToggleArchive = () => {
    const action = isArchived ? "unarchive" : "archive";
    const mutationFn = () => toggleArchive({ conversationId });
    const successMessage = isArchived
      ? "Conversation restored from archive"
      : "Conversation archived";

    handleAction(action, mutationFn, successMessage);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          onClick={handleMarkReadUnread}
          disabled={isLoading === "mark as read" || isLoading === "mark as unread"}
          className="cursor-pointer"
        >
          {isLoading === "mark as read" || isLoading === "mark as unread" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : isRead ? (
            <Circle className="mr-2 h-4 w-4" />
          ) : (
            <CheckCircle className="mr-2 h-4 w-4" />
          )}
          <span>{isRead ? "Mark as Unread" : "Mark as Read"}</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleTogglePin}
          disabled={isLoading === "pin" || isLoading === "unpin"}
          className="cursor-pointer"
        >
          {isLoading === "pin" || isLoading === "unpin" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : isPinned ? (
            <PinOff className="mr-2 h-4 w-4" />
          ) : (
            <Pin className="mr-2 h-4 w-4" />
          )}
          <span>{isPinned ? "Unpin Conversation" : "Pin Conversation"}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleToggleArchive}
          disabled={isLoading === "archive" || isLoading === "unarchive"}
          className="cursor-pointer text-orange-600 focus:text-orange-600"
        >
          {isLoading === "archive" || isLoading === "unarchive" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : isArchived ? (
            <ArchiveRestore className="mr-2 h-4 w-4" />
          ) : (
            <Archive className="mr-2 h-4 w-4" />
          )}
          <span>{isArchived ? "Restore from Archive" : "Archive Conversation"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
